import { DATA_LANGUAGES, type Language } from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { KeyRoundIcon, LogOutIcon, type LucideIcon, MoonIcon, SunIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Segmented } from "@/components/ui/misc";
import { Select } from "@/components/ui/select";
import { setLanguage, toLanguage } from "@/i18n";
import { navigate, PageLink } from "@/lib/pages";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { authClient } from "./auth";
import { useTRPC } from "./trpc";
import { RANGES, useView } from "./view";

/** A page of the admin app, and which of the view's choices it follows. */
export interface AdminPage {
  path: string;
  key: string;
  icon: LucideIcon;
  render: () => ReactNode;
  installation?: boolean;
  range?: boolean;
}

/** The hosted installations first, in their usual order; others as core names them. */
const KNOWN = ["app", "staging", "demo"];

export function useInstallationLabel() {
  const { t } = useTranslation();
  return (name: string) =>
    KNOWN.includes(name) ? t(`adminApp.installations.${name as "app"}`) : name;
}

function InstallationSwitcher() {
  const { t } = useTranslation();
  const { installation, setInstallation } = useView();
  const label = useInstallationLabel();
  const trpc = useTRPC();
  const overview = useQuery(trpc.overview.queryOptions());
  const names = [
    ...new Set([...KNOWN, ...(overview.data?.installations.map((i) => i.name) ?? [])]),
  ];
  return (
    <Select
      size="sm"
      className="w-40"
      aria-label={t("adminApp.installation")}
      value={installation}
      onValueChange={setInstallation}
      options={names.map((name) => ({ value: name, label: label(name) }))}
    />
  );
}

function RangePicker() {
  const { t } = useTranslation();
  const { range, setRange } = useView();
  return (
    <Segmented
      aria-label={t("adminApp.range")}
      value={range}
      onChange={setRange}
      options={RANGES.map((value) => ({ value, label: t(`adminApp.ranges.${value}`) }))}
    />
  );
}

function SideLink({
  href,
  icon: Icon,
  active,
  children,
}: {
  href: string;
  icon: LucideIcon;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <PageLink
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        active
          ? "bg-surface text-foreground shadow-xs ring-1 ring-border"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon className={cn("size-[18px]", active && "text-brand")} aria-hidden />
      {children}
    </PageLink>
  );
}

/** The admin app's frame: the pages on the left, one row of choices above the page. */
export function Shell({
  pages,
  current,
  email,
  extra,
}: {
  pages: readonly AdminPage[];
  current: AdminPage | null;
  email: string;
  /** A page outside the navigation's list (Passkeys). */
  extra?: { title: string; content: ReactNode };
}) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { href } = useView();
  const dark =
    theme === "dark" || (theme === "system" && document.documentElement.classList.contains("dark"));
  const title = extra?.title ?? (current ? t(`adminApp.nav.${current.key as "overview"}`) : "");

  return (
    <div className="flex min-h-dvh min-w-[1024px]">
      <aside className="sticky top-0 flex h-dvh w-56 shrink-0 flex-col border-r border-border bg-sidebar">
        <div className="flex h-16 items-center gap-2 px-4">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="text-[15px] font-semibold tracking-tight">{t("adminApp.title")}</span>
        </div>
        <nav aria-label={t("adminApp.pages")} className="flex flex-1 flex-col gap-0.5 px-3 py-2">
          {pages.map((page) => (
            <SideLink
              key={page.key}
              href={href(page.path)}
              icon={page.icon}
              active={page === current}
            >
              {t(`adminApp.nav.${page.key as "overview"}`)}
            </SideLink>
          ))}
        </nav>
        <div className="flex flex-col gap-1 border-t border-border p-3">
          <SideLink href={href("/passkeys")} icon={KeyRoundIcon} active={!!extra}>
            {t("adminApp.nav.passkeys")}
          </SideLink>
          <div className="flex items-center gap-1 px-1 py-1">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={dark ? t("adminApp.themeLight") : t("adminApp.themeDark")}
              onClick={() => setTheme(dark ? "light" : "dark")}
            >
              {dark ? <SunIcon /> : <MoonIcon />}
            </Button>
            <Select
              size="sm"
              className="w-auto flex-1"
              aria-label={t("adminApp.language")}
              value={toLanguage(i18n.language)}
              onValueChange={(language: Language) => setLanguage(language)}
              // The admin app is in English and German only (ADR 0020).
              options={DATA_LANGUAGES.map((code) => ({ value: code, label: code.toUpperCase() }))}
            />
          </div>
          <p className="truncate px-2.5 pt-1 text-xs text-muted-foreground" title={email}>
            {email}
          </p>
          <button
            type="button"
            onClick={async () => {
              await authClient.signOut();
              navigate("/sign-in", { replace: true });
            }}
            className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LogOutIcon className="size-[18px]" aria-hidden />
            {t("adminApp.signOut")}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-md">
          <div className="flex h-16 items-center gap-4 px-8">
            <h1 className="mr-auto text-lg font-semibold tracking-tight">{title}</h1>
            {current?.range ? <RangePicker /> : null}
            {current?.installation ? <InstallationSwitcher /> : null}
          </div>
        </header>
        <main className="min-w-0 flex-1 px-8 py-6">{extra?.content ?? current?.render()}</main>
      </div>
      <Toaster
        theme={theme}
        position="bottom-right"
        toastOptions={{
          classNames: {
            toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
          },
        }}
      />
    </div>
  );
}
